import { HomeIcon, HeartHandshake, Users, ShieldCheck, Stethoscope, ArrowUpRight } from "lucide-react";

const programs = [
  {
    icon: HomeIcon,
    title: "Family Support & Stability",
    blurb:
      "Resource navigation, youth mentoring, and household stability support that helps families stay rooted and resilient.",
    bullets: ["Family resource navigation", "Youth mentoring & development", "Financial & household stability", "Emergency wellness outreach"],
    tone: "primary",
    span: "md:col-span-7",
  },
  {
    icon: HeartHandshake,
    title: "Caregiver Support & Respite Relief",
    blurb:
      "Because those who carry others deserve to be carried, too — peer support, respite, and tools to sustain the journey.",
    bullets: ["Peer support & wellness check-ins", "Respite programs", "Stress management workshops"],
    tone: "image",
    image:
      "https://plus.unsplash.com/premium_photo-1663047695200-a4b6945b2074?fm=jpg&q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0",
    span: "md:col-span-5",
  },
  {
    icon: ShieldCheck,
    title: "Veteran Support & Resource Navigation",
    blurb:
      "Connecting disabled veterans and their families to healthcare, services, and the community that owes them honor.",
    bullets: ["Resource navigation", "Healthcare & service connection", "Future VA partnerships"],
    tone: "image",
    image:
      "https://images.unsplash.com/photo-1761839258605-d1b118266ccc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHx2ZXRlcmFuJTIwY29tbXVuaXR5JTIwc3VwcG9ydHxlbnwwfHx8fDE3Nzg2OTA4NDh8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
  },
  {
    icon: Users,
    title: "Community Engagement & Outreach",
    blurb:
      "Tangible care that meets real needs — food, essentials, holidays, school readiness, and senior wellness checks.",
    bullets: ["Food & essentials distribution", "Holiday & school readiness drives", "Senior wellness check-ins", "Intergenerational gatherings"],
    tone: "secondary",
    span: "md:col-span-7",
  },
  {
    icon: Stethoscope,
    title: "Health Advocacy & Family Wellness",
    blurb:
      "Education, advocacy, and emotional wellness for families living with chronic illness — sickle cell, lupus, ADHD, and more.",
    bullets: ["Chronic illness advocacy", "Community health awareness", "Emotional wellness support", "Healthcare partnerships"],
    tone: "accent",
    span: "md:col-span-12",
  },
];

export default function Programs() {
  return (
    <section id="programs" data-testid="programs-section" className="bg-[#EBE6DC] border-y border-[#D5C9B3]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-[#8B2635] mb-4">
              Our Programs
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2A29] leading-[1.05] tracking-tight">
              Five pillars of care, one ecosystem of grace.
            </h2>
          </div>
          <p className="text-[#5C5856] text-lg max-w-md">
            Every program is shaped by lived experience, faith, and a commitment to meeting families where they are.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {programs.map((p, i) => {
            const Icon = p.icon;
            const base = "relative rounded-3xl p-8 md:p-10 overflow-hidden group transition-all duration-300";
            if (p.tone === "image") {
              return (
                <article
                  key={p.title}
                  data-testid={`program-card-${i}`}
                  className={`${base} ${p.span} min-h-[340px] text-[#F4F1EB] flex flex-col justify-end`}
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(44,42,41,0.25) 0%, rgba(44,42,41,0.85) 100%), url(${p.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#F4F1EB] text-[#692128] flex items-center justify-center mb-5">
                    <Icon size={20} strokeWidth={2} />
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl leading-snug mb-3">{p.title}</h3>
                  <p className="text-[#F4F1EB]/85 leading-relaxed text-base mb-4">{p.blurb}</p>
                  <ul className="flex flex-wrap gap-2">
                    {p.bullets.map((b) => (
                      <li key={b} className="text-xs tracking-wide px-3 py-1 rounded-full bg-[#F4F1EB]/15 backdrop-blur-sm">
                        {b}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            }
            const colors = {
              primary: "bg-[#FAF8F5] border border-[#E2DCD0] text-[#2C2A29]",
              secondary: "bg-[#692128] text-[#F4F1EB]",
              accent: "bg-[#2C2A29] text-[#F4F1EB]",
            }[p.tone];
            const iconWrap = {
              primary: "bg-[#692128] text-[#F4F1EB]",
              secondary: "bg-[#C89F5D] text-[#2C2A29]",
              accent: "bg-[#C89F5D] text-[#2C2A29]",
            }[p.tone];
            return (
              <article key={p.title} data-testid={`program-card-${i}`} className={`${base} ${p.span} ${colors}`}>
                <div className={`w-12 h-12 rounded-full ${iconWrap} flex items-center justify-center mb-6`}>
                  <Icon size={20} strokeWidth={2} />
                </div>
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-serif text-2xl sm:text-3xl leading-snug max-w-md">{p.title}</h3>
                  <ArrowUpRight size={20} className="opacity-50 group-hover:opacity-100 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                </div>
                <p className={`mt-3 leading-relaxed text-base ${p.tone === "primary" ? "text-[#5C5856]" : "opacity-90"}`}>
                  {p.blurb}
                </p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className={`text-sm flex items-start gap-2 ${
                        p.tone === "primary" ? "text-[#2C2A29]/80" : "text-[#F4F1EB]/90"
                      }`}
                    >
                      <span className={`mt-2 w-1.5 h-1.5 rounded-full ${p.tone === "primary" ? "bg-[#C89F5D]" : "bg-[#C89F5D]"}`} />
                      {b}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
